import { cn } from '@/lib/utils';

interface HyggeLogoProps {
  className?: string;
  variant?: 'full' | 'icon';
}

export function HyggeLogo({ className, variant = 'full' }: HyggeLogoProps) {
  if (variant === 'icon') {
    return (
      <svg
        className={cn('h-10 w-auto', className)}
        width="54"
        height="50"
        viewBox="0 0 54 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Hygge Software"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 25.0464L25.5425 0L37.557 11.7811L34.6244 14.6568L45.1252 24.9536L34.6244 35.2504L37.557 38.1262L25.4479 50L0 25.0464ZM24.1111 11.1318L27.9114 14.6766L17.2519 25.22L27.9114 35.7634L24.1111 39.399L9.74383 25.22L24.1111 11.1318Z"
          // The brand mint, as on hygge.software, in both themes. [HW-926]
          className="fill-cta"
        />
        <path
          className="fill-foreground"
          d="M36.2048 14.5958L40.0052 11.051L54.3724 25.1392L40.0052 39.3182L36.2048 35.6826L46.8644 25.1392L36.2048 14.5958Z"
        />
      </svg>
    );
  }

  return (
    <svg
      className={cn('h-12 w-auto', className)}
      width="138"
      height="50"
      viewBox="0 0 138 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="HyggeOS"
    >
      <g clipPath="url(#clip0_692_14604)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 25.0464L25.5425 0L37.557 11.7811L34.6244 14.6568L45.1252 24.9536L34.6244 35.2504L37.557 38.1262L25.4479 50L0 25.0464ZM24.1111 11.1318L27.9114 14.6766L17.2519 25.22L27.9114 35.7634L24.1111 39.399L9.74383 25.22L24.1111 11.1318Z"
          // The brand mint, as on hygge.software, in both themes. [HW-926]
          className="fill-cta"
        />
        <path
          className="fill-foreground"
          d="M36.2048 14.5958L40.0052 11.051L54.3724 25.1392L40.0052 39.3182L36.2048 35.6826L46.8644 25.1392L36.2048 14.5958Z"
        />
        <path className="fill-foreground" d="M66.2898 11.0293H61.8208V38.9705H66.2898V11.0293Z" />
        <path className="fill-foreground" d="M72.9931 11.0293H68.5242V38.9705H72.9931V11.0293Z" />
        <path className="fill-foreground" d="M61.8208 22.794V27.2058H72.9932V22.794H61.8208Z" />
        <path
          className="fill-foreground"
          d="M73.738 11.0293L77.2767 26.4012C77.4004 26.9359 77.4622 27.4825 77.4622 28.0308V38.9705H81.9311V28.0308C81.9311 27.4825 81.993 26.9359 82.1166 26.4012L85.6553 11.0293H81.1863L79.8441 20.3044C79.8196 20.4727 79.5738 20.4727 79.5492 20.3044L78.207 11.0293H73.738Z"
        />
        <path
          className="fill-foreground"
          d="M91.6288 11.0293C96.0977 11.0293 97.215 15.0734 97.215 16.9116H93.8528C93.8528 16.544 93.4804 14.3381 91.6184 14.3381C90.5011 14.3381 89.7563 15.0734 89.7563 15.8087V17.6469V19.4852C89.7563 20.2205 89.7563 20.9558 91.6184 20.9558C93.4804 20.9558 93.7284 19.7303 93.8528 19.1175H91.6288V17.6469H97.215V24.2646H93.8528V21.3234C93.8528 24.2646 90.8735 24.2646 89.7563 24.2646C86.0321 24.2646 86.4046 17.6469 86.415 16.9116C86.4254 16.1764 86.415 11.0293 91.6288 11.0293Z"
        />
        <path
          className="fill-foreground"
          d="M91.6288 25.7354C96.0977 25.7354 97.215 29.7795 97.215 31.6177H93.8528C93.8528 31.2501 93.4804 29.0442 91.6184 29.0442C90.5011 29.0442 89.7563 29.7795 89.7563 30.5148V32.353V34.1912C89.7563 34.9265 89.7563 35.6618 91.6184 35.6618C93.4804 35.6618 93.7284 34.4363 93.8528 33.8236H91.6288V32.353H97.215V38.9706H93.8528V36.0295C93.8528 38.9706 90.8735 38.9706 89.7563 38.9706C86.0321 38.9706 86.4046 32.353 86.415 31.6177C86.4254 30.8824 86.415 25.7354 91.6288 25.7354Z"
        />
        <path
          className="fill-foreground"
          d="M108 15.4411V11.0293H99.062V34.5587C99.062 37.8511 100.168 38.55 101.142 38.7945C101.732 38.9427 102.349 38.9705 102.958 38.9705H108V34.5587H103.531V27.2058H108V22.794H103.531V15.4411H108Z"
        />
      </g>
      <text
        // Accent text, not the mint: a light background cannot carry mint
        // lettering (1.8:1). [HW-926]
        className="fill-hygge"
        x="112"
        y="39"
        fontFamily="Poppins, system-ui, sans-serif"
        fontSize="16"
        fontWeight="700"
        letterSpacing="0.3"
      >
        OS
      </text>
      <defs>
        <clipPath id="clip0_692_14604">
          <rect width="108" height="50" />
        </clipPath>
      </defs>
    </svg>
  );
}
